import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { IconButton, InputAdornment, Stack, TextField } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";
import { ClearIcon } from "@mui/x-date-pickers";
import { onSearchStore } from "@shared/libs/Global";
import SnComboBox from "./SnComboBox";

interface SnComboTextBoxProps {
  defaultValue?: string;
  format?: string;
  code?: string;
  sql?: string;
  emptyLine?: boolean;
  onValueChanged?: (combo: string, text: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  popupRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  isFormPanel?: boolean;
  onFocus?: (target: string) => void;
  js_combo?: [];
}

/**
 * 문자열에 포맷 적용
 * @param value 문자열
 * @param format 포맷
 */
const applyFormat = (value: string, format: string) => {
  let formatted = "";
  let valueIndex = 0;

  for (let i = 0; i < format.length; i++) {
    if (valueIndex >= value.length) break; // 입력값이 끝나면 중지

    if (format[i] === "#") {
      formatted += value[valueIndex]; // 입력값 삽입
      valueIndex++;
    } else {
      formatted += format[i]; // 고정된 문자 삽입
    }
  }

  return formatted;
};

const SnComboTextBox = React.memo(
  ({
    defaultValue,
    format,
    code,
    sql,
    emptyLine,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    popupRef,
    inputRef,
    isSearchPanel = false,
    isFormPanel = false,
    onFocus,
    js_combo,
  }: SnComboTextBoxProps) => {
    // combo 세팅
    const defaultValueCombo: string = defaultValue
      ? defaultValue.split(",")[0].trim()
      : "";
    // 콤보 옵션 조회/표시는 SnComboBox가 담당하고, 여기서는 선택된 코드값만 관리한다.
    const [comboValue, setComboValue] = useState<string>(defaultValueCombo);

    // text 세팅
    let defaultValueText: string = defaultValue
      ? defaultValue.split(",")[1].trim()
      : "";
    defaultValueText =
      format != null && format.length > 0
        ? applyFormat(
            defaultValueText && defaultValueText != ""
              ? defaultValueText.replace(/[^0-9]/g, "")
              : "",
            format
          )
        : defaultValueText;
    const [textValue, setTextValue] = useState<string>(defaultValueText);
    const textRef = useRef<HTMLInputElement | null>(null);

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    // SearchPanel에서 enter 누르면 실행할 함수
    const { runOnSearch } = onSearchStore();

    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue !== undefined) {
          const defaultValueCombo: string = defaultValue.split(",")[0];
          const defaultValueText: string = defaultValue.split(",")[1].trim();
          setComboValue(defaultValueCombo);
          setTextValue(
            format && format.length > 0
              ? applyFormat(
                  defaultValueText && defaultValueText != ""
                    ? defaultValueText.replace(/[^0-9]/g, "")
                    : "",
                  format
                )
              : defaultValueText
          );
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    // SnComboBox에서 값이 바뀌면 호출된다. (top1 첫 항목 선택 포함)
    // SnComboBox는 defaultValue 반영 시에도 onValueChanged를 호출하므로 같은 값이면 무시한다.
    const handleComboChange = (newValue: string) => {
      const updatedValue = newValue || "";
      if (updatedValue === comboValue) return;
      setComboValue(updatedValue);

      if (onValueChanged) {
        onValueChanged(updatedValue, textValue);
      }
    };

    const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
      let formattedValue: any = e.target.value;
      if (format) {
        formattedValue = applyFormat(
          formattedValue && formattedValue != ""
            ? formattedValue.replace(/[^0-9]/g, "")
            : "",
          format
        );
      }
      setTextValue(formattedValue);
    };

    const handleBlur = () => {
      setFocused(false);
      // 포커스가 빠졌을 때만 호출
      if (onValueChanged) {
        onValueChanged(
          comboValue,
          format ? textValue.replace(/[^0-9]/g, "") : textValue
        );
      }
    };

    const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (popupRef) {
          // 팝업일 때
          popupRef.current.onSearch(undefined);
        } else if (isSearchPanel) {
          // searchPanel일 때
          if (onValueChanged) {
            onValueChanged(
              comboValue,
              format ? textValue.replace(/[^0-9]/g, "") : textValue
            );
          }
          runOnSearch();
          // onFocus && onFocus("current");
          textRef.current?.focus();
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    const handleClear = () => {
      setTextValue("");
      // focus 유지
      textRef.current?.focus();
    };

    return (
      <div>
        <Stack direction="row" alignItems="center">
          {/* 콤보 영역: 공통 SnComboBox 사용. blur는 버블링으로 받아 기존 handleBlur를 유지한다. */}
          <div
            style={{ width: 110, marginRight: "10px", flexShrink: 0 }}
            onBlur={handleBlur}
          >
            <SnComboBox
              defaultValue={comboValue}
              code={code}
              sql={sql}
              emptyLine={emptyLine}
              js_combo={js_combo}
              isReadOnly={isReadOnly}
              isSearchPanel={isSearchPanel}
              isFormPanel={isFormPanel}
              showCode={false}
              disableClearable={true}
              onValueChanged={handleComboChange}
              // 콤보 선택/Enter/Tab 후에는 오른쪽 텍스트 입력으로 포커스 이동
              onFocus={() => textRef.current?.focus()}
            />
          </div>
          <div style={{ flex: 1, display: "flex" }} onKeyDown={handleKeyDown}>
            <TextField
              // SearchPanel 포커스 대상은 콤보가 아니라 이 입력칸. 콤보에 달면 팝업이 열릴 때 콤보가 잡힌다.
              inputRef={(el: HTMLInputElement | null) => {
                textRef.current = el;
                if (typeof inputRef === "function") inputRef(el);
                else if (inputRef) inputRef.current = el;
              }}
              value={textValue}
              slotProps={{
                input: {
                  autoComplete: "off",
                  style: {
                    height: StyleSettings.control_height,
                    fontSize: StyleSettings.font,
                  },
                  endAdornment: !isReadOnly &&
                    (isFocused || isMouseOver) &&
                    textValue && (
                      <InputAdornment
                        position="end"
                        sx={{ paddingRight: "5px" }}
                        tabIndex={-1}
                      >
                        <IconButton onClick={handleClear} size="small">
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                },
              }}
              onChange={handleTextChange}
              onBlur={handleBlur}
              onFocus={() => setFocused(true)}
              onMouseOver={() => setMouseOver(true)}
              onMouseOut={() => setMouseOver(false)}
              disabled={isReadOnly}
              sx={{
                flex: 1,
                width: "100%",
                backgroundColor: backgroundColor,
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderWidth: borderWidth, // 테두리 두께 설정
                  },
                  "&:hover fieldset": {
                    borderWidth: borderWidth, // 호버 시 두께 유지
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: borderWidth, // 포커스 시 두께 유지
                  },
                },
                "& .MuiInputBase-root": {
                  paddingRight: 0,
                  "&.Mui-disabled": {
                    backgroundColor: "#f5f5f5",
                  },
                },
              }}
            />
          </div>
        </Stack>
      </div>
    );
  }
);

export default SnComboTextBox;
